import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (mobileRef.current && !mobileRef.current.contains(e.target as Node)) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setDropdownOpen(false);
  }, [location]);

  const handleCategorySelect = (url: string) => {
    setDropdownOpen(false);
    setMobileMenuOpen(false);
    window.open(url, '_blank', 'noopener,nofollow');
  };

  const isHome = location.pathname === '/';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-black/25 backdrop-blur-sm'
      }`}
    >
      <div className="w-full px-4 md:px-10">
        <div className="flex items-center justify-between h-14 md:h-[4.5rem]">
          {/* Logo */}
          <Link
            to="/"
            className={`font-heading text-xl md:text-2xl font-bold tracking-tight transition-all duration-300 ${
              scrolled ? 'text-primary-700' : 'text-white drop-shadow-sm'
            }`}
          >
            itaobuy
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6">
            <Link
              to="/"
              className={`text-sm font-medium transition-colors duration-300 whitespace-nowrap ${
                scrolled ? 'text-foreground-700 hover:text-primary-600' : 'text-white/90 hover:text-white drop-shadow-sm'
              }`}
            >
              Home
            </Link>

            {isHome ? (
              <a
                href="#shop-categories"
                className={`text-sm font-medium transition-colors duration-300 whitespace-nowrap ${
                  scrolled ? 'text-foreground-700 hover:text-primary-600' : 'text-white/90 hover:text-white drop-shadow-sm'
                }`}
              >
                Shopping Guide
              </a>
            ) : (
              <Link
                to="/#shop-categories"
                className={`text-sm font-medium transition-colors duration-300 whitespace-nowrap ${
                  scrolled ? 'text-foreground-700 hover:text-primary-600' : 'text-white/90 hover:text-white drop-shadow-sm'
                }`}
              >
                Shopping Guide
              </Link>
            )}

            <Link
              to="/itaobuy-spreadsheet-guide"
              className={`text-sm font-medium transition-colors duration-300 whitespace-nowrap ${
                scrolled ? 'text-foreground-700 hover:text-primary-600' : 'text-white/90 hover:text-white drop-shadow-sm'
              }`}
            >
              Guide
            </Link>

            {/* Categories Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`flex items-center gap-1.5 text-sm font-medium transition-colors duration-300 whitespace-nowrap cursor-pointer ${
                  scrolled ? 'text-foreground-700 hover:text-primary-600' : 'text-white/90 hover:text-white drop-shadow-sm'
                }`}
              >
                Categories
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 w-72 bg-background-50 rounded-xl border border-background-200 shadow-lg py-2 max-h-96 overflow-y-auto">
                  {categories.map((cat) => (
                    <button
                      key={cat.name}
                      onClick={() => handleCategorySelect(cat.url)}
                      className="w-full text-left px-4 py-2.5 text-sm text-foreground-800 hover:bg-primary-50 hover:text-primary-700 transition-colors duration-150 flex items-center gap-3 cursor-pointer whitespace-nowrap"
                    >
                      <span className="w-5 h-5 flex items-center justify-center text-primary-500 shrink-0">
                        <i className={cat.icon}></i>
                      </span>
                      {cat.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`px-5 py-2 rounded-full text-sm font-semibold border transition-all duration-300 whitespace-nowrap cursor-pointer ${
                scrolled
                  ? 'border-primary-500 text-primary-600 hover:bg-primary-500 hover:text-background-50'
                  : 'border-white/60 text-white hover:bg-white hover:text-primary-700'
              }`}
            >
              itaobuy sheet
            </a>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                scrolled
                  ? 'bg-primary-500 text-background-50 hover:bg-primary-600'
                  : 'bg-white text-primary-700 hover:bg-white/90'
              }`}
            >
              buy it now
            </a>
          </div>

          {/* Mobile Hamburger */}
          <div className="md:hidden flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                scrolled
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-white text-primary-700'
              }`}
            >
              buy it now
            </a>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`w-9 h-9 flex items-center justify-center rounded-lg transition-colors duration-200 cursor-pointer ${
                scrolled ? 'text-foreground-700 hover:bg-background-100' : 'text-white hover:bg-white/10'
              }`}
            >
              <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Panel */}
      {mobileMenuOpen && (
        <div
          ref={mobileRef}
          className="md:hidden bg-background-50 border-t border-background-200 shadow-lg max-h-[calc(100vh-3.5rem)] overflow-y-auto"
        >
          <div className="px-4 py-3 space-y-1">
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-foreground-800 hover:bg-primary-50 hover:text-primary-700 transition-colors duration-150"
            >
              <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                <i className="ri-home-line"></i>
              </span>
              Home
            </Link>

            {isHome ? (
              <a
                href="#shop-categories"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-foreground-800 hover:bg-primary-50 hover:text-primary-700 transition-colors duration-150"
              >
                <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                  <i className="ri-shopping-bag-line"></i>
                </span>
                Shopping Guide
              </a>
            ) : (
              <Link
                to="/#shop-categories"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-foreground-800 hover:bg-primary-50 hover:text-primary-700 transition-colors duration-150"
              >
                <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                  <i className="ri-shopping-bag-line"></i>
                </span>
                Shopping Guide
              </Link>
            )}

            <Link
              to="/itaobuy-spreadsheet-guide"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-foreground-800 hover:bg-primary-50 hover:text-primary-700 transition-colors duration-150"
            >
              <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                <i className="ri-book-open-line"></i>
              </span>
              Guide
            </Link>
          </div>

          <div className="border-t border-background-200 mx-4"></div>
          <div className="px-4 py-3">
            <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wider px-3 mb-2">Categories</p>
            <div className="space-y-0.5">
              {categories.map((cat) => (
                <button
                  key={cat.name}
                  onClick={() => handleCategorySelect(cat.url)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-800 hover:bg-primary-50 hover:text-primary-700 transition-colors duration-150 cursor-pointer"
                >
                  <span className="w-5 h-5 flex items-center justify-center text-primary-500 shrink-0">
                    <i className={cat.icon}></i>
                  </span>
                  <span>{cat.name}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="border-t border-background-200 mx-4"></div>
          <div className="px-4 py-4">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center justify-center gap-2 w-full px-4 py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors duration-200 cursor-pointer"
            >
              itaobuy sheet
              <i className="ri-arrow-right-up-line"></i>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}